




























































































































































/* eslint-disable no-console */

import { Component, Prop, Vue } from 'vue-property-decorator';
import urlJoin from 'url-join';
import linkifyHtml from 'linkifyjs/html';
const FileSaverAsync = () => import('file-saver');
import Clipboard from 'clipboard';
import * as fileType from 'file-type/browser';
import {blobToUint8Array} from 'binconv/dist/src/blobToUint8Array';
import {uint8ArrayToBlob} from 'binconv/dist/src/uint8ArrayToBlob';
import {blobToReadableStream} from 'binconv/dist/src/blobToReadableStream';
import {mdiAlert, mdiCheck, mdiChevronDown, mdiContentSave, mdiCloseCircle, mdiEye, mdiEyeOff, mdiKey, mdiFeatureSearchOutline} from "@mdi/js";

import {globalStore} from "@/vue-global";
import {strings} from "@/strings";
import * as utils from '@/utils';
import * as pipingUiUtils from "@/piping-ui-utils";
import AsyncComputed from 'vue-async-computed-decorator';
import {Protection, VerificationStep, VerifiedParcel, verifiedParcelFormat} from "@/datatypes";
import VerificationCode from "@/components/VerificationCode.vue";


export type DataViewerProps = {
  viewNo: number,
  serverUrl: string,
  secretPath: string,
  protection: Protection,
};

// NOTE: Automatically download when mounted
@Component({
  components: {
    VerificationCode,
  },
})
export default class DataViewer extends Vue {
  @Prop() private props!: DataViewerProps;

  // Progress bar setting
  private progressSetting: {loadedBytes: number, totalBytes?: number} = {
    loadedBytes: 0,
    totalBytes: undefined,
  };

  private readableBytesString = utils.readableBytesString;

  private errorMessage: () => string = () => "";
  private xhr: XMLHttpRequest;
  private isDoneDownload: boolean = false;
  private canceled: boolean = false;
  private imgSrc: string = '';
  private videoSrc: string = '';
  private text: string = '';
  private enablePasswordReinput: boolean = false;
  private showsPassword: boolean = false;
  private verificationStep: VerificationStep = {type: 'initial'};

  private rawBlob: Blob = new Blob();
  private blob: Blob = new Blob();

  private showsCopied: boolean = false;
  private isDecrypting: boolean = false;

  private icons = {
    mdiContentSave,
    mdiCloseCircle,
    mdiEye,
    mdiEyeOff,
    mdiKey,
    mdiFeatureSearchOutline,
  };

  // for language support
  private get strings() {
    return strings(globalStore.language);
  }

  private get progressPercentage(): number | null {
    if (this.isDoneDownload) {
      return 100;
    } else if (this.progressSetting.totalBytes === undefined) {
      return null;
    } else if (this.progressSetting.totalBytes === 0) {
      return 100;
    } else {
      return this.progressSetting.loadedBytes / this.progressSetting.totalBytes * 100;
    }
  }

  private get hasError(): boolean {
    return this.errorMessage() !== "";
  }

  private get headerIcon(): string {
    if (this.hasError) {
      return mdiAlert;
    } else if (this.canceled) {
      return mdiCloseCircle;
    } else if (this.isDoneDownload) {
      return mdiCheck;
    } else {
      return mdiChevronDown;
    }
  }

  private get headerIconColor(): string | undefined {
    if (this.hasError) {
      return "error";
    } else if (this.canceled) {
      return "warning";
    } else if (this.isDoneDownload) {
      return "teal";
    } else {
      return undefined
    }
  }

  private get isCancelable(): boolean {
    return this.isReadyToDownload && !this.isDoneDownload && !this.hasError && !this.canceled;
  }

  private get isReadyToDownload(): boolean {
    return this.props.protection.type === 'passwordless' ? this.verificationStep.type === 'verified' && this.verificationStep.verified : true
  }

  private get downloadPath(): string {
    return urlJoin(this.props.serverUrl, this.props.secretPath);
  }

  @AsyncComputed()
  private async linkifiedText(): Promise<string> {
    return utils.sanitizeHtmlAllowingATag(linkifyHtml(this.text, {
      defaultProtocol: 'https'
    }));
  }

  constructor() {
    super();
    this.xhr = new XMLHttpRequest();
  }

  async mounted() {
    // Scroll to this element
    // NOTE: no need to add `await`
    pipingUiUtils.scrollTo(this.$el);

    // Setting for copying to clipboard
    new Clipboard((this.$refs.text_copy_button as Vue).$el, {
      target: () => {
        this.showsCopied = true;
        setTimeout(() => {
          this.showsCopied = false;
        }, 2000);
        return this.$refs.text_viewer as Element
      }
    });

    // Key exchange
    const keyExchangeRes = await pipingUiUtils.keyExchangeAndReceiveVerified(
      this.props.serverUrl,
      this.props.secretPath,
      this.props.protection,
      (step: VerificationStep) => {
        this.verificationStep = step;
      }
    );

    // If error
    if (keyExchangeRes.type === "error") {
      this.errorMessage = () => keyExchangeRes.errorMessage(globalStore.language);
      return;
    }
    const {key} = keyExchangeRes;

    this.xhr.open('GET', this.downloadPath);
    this.xhr.responseType = 'blob';
    this.xhr.onprogress = (ev) => {
      console.log(`Download: ${ev.loaded}`)
    };
    this.xhr.onreadystatechange = (ev) => {
      if (this.xhr.readyState === XMLHttpRequest.HEADERS_RECEIVED) {
        const length: string | null = this.xhr.getResponseHeader('Content-Length');
        if (length !== null) {
          this.progressSetting.totalBytes = parseInt(length, 10);
        }
      }
    };
    this.xhr.onprogress = (ev) => {
      this.progressSetting.loadedBytes = ev.loaded;
    };
    this.xhr.onload = async (ev) => {
      if (this.xhr.status === 200) {
        this.isDoneDownload = true;
        // Get raw response body
        this.rawBlob = this.xhr.response;
        // Decrypt and view blob if possible
        this.decryptIfNeedAndViewBlob(key);
      } else {
        const responseText = await utils.readBlobAsText(this.xhr.response);
        this.errorMessage = () => this.strings['xhr_status_error']({
          status: this.xhr.status,
          response: responseText,
        });
      }
    };
    this.xhr.onerror = () => {
      this.errorMessage = () => this.strings['data_viewer_xhr_onerror'];
    };
    this.xhr.send();
  }

  private async viewBlob() {
    // Reset viewers
    this.imgSrc = '';
    this.videoSrc = '';
    this.text = '';

    const isText: boolean = await (async () => {
      // NOTE: 4100 was used in FileType.minimumBytes in file-type until 13.1.2
      const nBytes = 4100;
      // Get first bytes from blob
      const firstChunk: Uint8Array = await blobToUint8Array(this.blob.slice(0, nBytes));
      return utils.isText(firstChunk);
    })();

    // If body is text
    if (isText) {
      // Set text
      this.text = await utils.readBlobAsText(this.blob);
    } else {
      // Detect type of blob
      const fileTypeResult = await fileType.fromStream(blobToReadableStream(this.blob));
      if (fileTypeResult !== undefined) {
        const blobUrl = URL.createObjectURL(this.blob);
        if (fileTypeResult.mime.startsWith("image/")) {
          this.imgSrc = blobUrl;
        } else if (fileTypeResult.mime.startsWith("video/")) {
          this.videoSrc = blobUrl;
        } else if (fileTypeResult.mime.startsWith("text/")) {
          // Set text
          this.text = await utils.readBlobAsText(this.blob);
        }
      }
    }

  }

  private async decryptIfNeedAndViewBlob(password: string | Uint8Array | undefined) {
    this.blob = await (async () => {
      if (password === undefined) {
        return this.rawBlob;
      } else {
        // Get response body
        const resBody = await blobToUint8Array(this.rawBlob);
        try {
          this.isDecrypting = true;
          // Decrypt the response body
          const plain = await utils.decrypt(resBody, password);
          this.enablePasswordReinput = false;
          this.errorMessage = () => '';
          return uint8ArrayToBlob(plain);
        } catch (err) {
          this.enablePasswordReinput = true;
          this.errorMessage = () => this.strings['password_might_be_wrong'];
          console.log('Decrypt error:', err);
          return new Blob();
        } finally {
          this.isDecrypting = false;
        }
      }
    })();

    // View blob if possible
    this.viewBlob();
  }

  private viewRaw() {
    this.blob = this.rawBlob;
    this.enablePasswordReinput = false;
    this.errorMessage = () => '';
    // View blob if possible
    this.viewBlob();
  }

  private cancelDownload(): void {
    this.xhr.abort();
    this.canceled = true;
  }

  private async save(): Promise<void> {
    const FileSaver = await FileSaverAsync();
    FileSaver.saveAs(this.blob, this.props.secretPath);
  }
}
